import { useMutation } from '@tanstack/react-query';
import {
  getAllUnreadActivityIds,
  markActivitiesRead,
  markActivitiesUnread,
} from '../services/activityService';

interface MarkVariables {
  profileId: string;
  activityIds: string[];
}

/**
 * Mark one or more activities read.
 *
 * Deliberately does NOT invalidate/refetch the activity list: callers own when
 * the list reloads. The auto-read path in ActivityListingStatusCard marks a
 * stale listing's card read WHILE it's on screen and animates it out itself —
 * a refetch there would rip the card out mid-animation.
 */
export function useMarkActivitiesRead() {
  return useMutation({
    mutationFn: ({ profileId, activityIds }: MarkVariables) =>
      markActivitiesRead(profileId, activityIds),
  });
}

/**
 * Flip activities back to unread — the undo behind the "marked as read" toast.
 * Same no-auto-refetch contract as useMarkActivitiesRead: the caller reloads.
 */
export function useMarkActivitiesUnread() {
  return useMutation({
    mutationFn: ({ profileId, activityIds }: MarkVariables) =>
      markActivitiesUnread(profileId, activityIds),
  });
}

/**
 * Mark every unread activity read. Resolves to the ids it cleared so the caller
 * can offer an undo over exactly that set — the list has usually only paged in
 * the first 20, so the ids are enumerated server-side first rather than taken
 * from what happens to be on screen.
 */
export function useMarkAllActivitiesRead() {
  return useMutation({
    mutationFn: async (profileId: string): Promise<string[]> => {
      const ids = await getAllUnreadActivityIds(profileId);
      if (ids.length > 0) await markActivitiesRead(profileId, ids);
      return ids;
    },
  });
}
